import React, { useEffect, useMemo, useState } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_ORDER_ROUTER, API_BRANCH_ROUTER } from "../../../services/api-config";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import CookieUtils from "../../../utils/cookie-utils";
import { ORDER_STATUS, OrderItem } from "../services/order-service";
import { formatServerDateTime } from "../utils/datetime";

const TRANSFER_STATUS = { PENDING: 1, IN_TRANSIT: 2, RECEIVED: 3, CANCELLED: 4 };
const TRANSFER_DIRECTION = { TO_BRANCH: 1, RETURN: 2 };
const STATUS_STYLE: Record<number, string> = {
  1: "bg-amber-100 text-amber-700",
  2: "bg-orange-100 text-orange-700",
  3: "bg-green-100 text-green-700",
  4: "bg-slate-100 text-slate-500",
};
const STATUS_LABEL: Record<number, string> = {
  1: "Chờ giao xe",
  2: "Đang trên đường",
  3: "Đã nhận",
  4: "Đã huỷ",
};
// Loại A (WAITING_ACCEPT — đơn chưa ai lấy hàng) không có bước "giao xe" (không có gì để chở vật lý) —
// nhãn PENDING rõ nghĩa hơn khi gọi là "Chờ xác nhận" (chờ chi nhánh nhận bấm xác nhận nhận đơn).
const getTransferStatusLabel = (status: number, preTransferStatus?: number | null): string => {
  if (status === TRANSFER_STATUS.PENDING && preTransferStatus === ORDER_STATUS.WAITING_ACCEPT) return "Chờ xác nhận";
  return STATUS_LABEL[status] || "—";
};

// Các bước cho tạo phiếu (điều chuyển tự do):
//  - WAITING_ACCEPT (Loại A — đơn chưa ai lấy hàng): chuyển nguyên đơn cho chi nhánh khác tự lấy/giặt/giao.
//  - INSPECTING / WAITING_LAUNDRY (Loại B — đồ đã hiện diện vật lý): còn dời được. Muốn chi nhánh khác giao thì
//    forward TRƯỚC khi "Sẵn sàng giao"; đã sẵn sàng giao → chốt chi nhánh đang giữ đồ giao, KHÔNG điều chuyển nữa.
const ELIGIBLE_STATUSES = new Set<number>([
  ORDER_STATUS.WAITING_ACCEPT, ORDER_STATUS.INSPECTING, ORDER_STATUS.WAITING_LAUNDRY,
]);

interface TransferItem {
  id: number;
  from_branch_id: number;
  to_branch_id: number;
  from_branch_name?: string;
  to_branch_name?: string;
  direction: number;
  status: number;
  pre_transfer_status?: number | null;
  manifest?: { category: string; quantity: number }[] | null;
  note?: string | null;
  sent_at?: string | null;
  received_at?: string | null;
  created_at?: string;
  // Tài xế vận chuyển giữa 2 chi nhánh (Loại B) — chỉ tự claim/được chỉ định qua app, web chỉ hiển thị.
  transport_driver_id?: number | null;
  transport_driver_name?: string | null;
}

interface Props {
  order: OrderItem;
  onReload?: () => void;
}

/**
 * Khối "Điều chuyển chi nhánh" trong chi tiết đơn (web):
 *  - Tạo phiếu (giám sát, đơn đang giữ đồ) → chọn chi nhánh nhận + ghi chú.
 *  - Thao tác phiếu đang chạy: "Đã giao xe" / "Xác nhận đã nhận" / "Huỷ" theo vai trò gửi/nhận.
 *  - Timeline: toàn bộ phiếu (from→to, trạng thái, thời gian).
 */
const OrderTransferSection: React.FC<Props> = ({ order, onReload }) => {
  const [transfers, setTransfers] = useState<TransferItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [note, setNote] = useState("");
  // Chi nhánh mà tài khoản hiện tại thuộc về — nguồn tin cậy để gating gửi/nhận
  // (cookie "user" trên web KHÔNG chứa branch_ids nên phải hỏi my-branches).
  const [myBranchIds, setMyBranchIds] = useState<Set<number>>(new Set());

  const { request: getTransfers } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const { request: postOrder } = useFetchData.Post<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const { request: getBranches } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.USER_SERVICE });

  const user = CookieUtils.getCurrentUser();
  const codes: string[] = user?.privilege_codes || [];
  const isSupervisor = codes.includes("OPERATION_SUPERVISOR") || codes.includes("ADMIN");
  // Người dùng chỉ được thao tác gửi/nhận ở chi nhánh MÌNH thuộc về.
  // all-branch → my-branches trả tất cả nên tự động phủ hết.
  const branchAllowed = (bid: number) => myBranchIds.has(bid);

  const ownerBranchId = Number(order?.branch_id) || 0;
  const currentBranchId = Number(order?.current_branch_id) || ownerBranchId;

  const manifest = useMemo(() => {
    const cc = order?.classification_category || "";
    const items: { category: string; quantity: number }[] = [];
    const regex = /([^(,]+)\((\d+)\)/g;
    let m;
    while ((m = regex.exec(cc)) !== null) items.push({ category: m[1].trim(), quantity: Number(m[2]) });
    return items;
  }, [order?.classification_category]);

  const loadTransfers = async () => {
    if (!order?.id) return;
    setLoading(true);
    try {
      const res = await getTransfers({ endPoint: API_ORDER_ROUTER.API_ORDER_TRANSFERS(order.id) });
      if (res && isSuccess(res)) {
        const raw: any = res.data;
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setTransfers(list);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTransfers(); /* eslint-disable-next-line */ }, [order?.id]);

  // Nạp chi nhánh của tài khoản hiện tại (để gating gửi/nhận đúng phía)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getBranches({ endPoint: API_BRANCH_ROUTER.API_MY_BRANCHES });
        if (mounted && res && isSuccess(res)) {
          const raw: any = res.data;
          const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : Array.isArray(raw?.list) ? raw.list : [];
          setMyBranchIds(new Set(list.map((b: any) => Number(b.id))));
        }
      } catch {
        // ignore
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const openCreate = async () => {
    setShowCreate(true);
    if (branches.length === 0) {
      try {
        // Điều chuyển có thể tới BẤT KỲ chi nhánh nào khác trong công ty (xưởng / cửa hàng khác),
        // không giới hạn theo chi nhánh của user → dùng danh sách toàn công ty.
        const res = await getBranches({ endPoint: API_BRANCH_ROUTER.API_LIST, data: { status: 1, company_id: 2000 } });
        if (res && isSuccess(res)) {
          const raw: any = res.data;
          const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : Array.isArray(raw?.list) ? raw.list : [];
          setBranches(list.filter((b: any) => Number(b.id) !== currentBranchId).map((b: any) => ({ id: b.id, name: b.name })));
        }
      } catch {
        showToast({ message: "Không tải được danh sách chi nhánh", type: "error" });
      }
    }
  };

  const active = transfers.find(t => t.status === TRANSFER_STATUS.PENDING || t.status === TRANSFER_STATUS.IN_TRANSIT);

  const doCreate = async () => {
    if (!selectedBranchId) { showToast({ message: "Chưa chọn chi nhánh nhận", type: "warning" }); return; }
    const direction = (selectedBranchId === ownerBranchId && currentBranchId !== ownerBranchId)
      ? TRANSFER_DIRECTION.RETURN : TRANSFER_DIRECTION.TO_BRANCH;
    setBusy(true);
    try {
      const res = await postOrder({
        endPoint: API_ORDER_ROUTER.API_CREATE_TRANSFER(order.id!),
        data: { to_branch_id: selectedBranchId, direction, manifest: manifest.length ? manifest : undefined, note: note || undefined },
      });
      if (res && isSuccess(res)) {
        showToast({ message: "Đã tạo phiếu điều chuyển", type: "success" });
        setShowCreate(false); setSelectedBranchId(null); setNote("");
        await loadTransfers(); onReload?.();
      } else {
        showToast({ message: (res as any)?.message || "Tạo phiếu thất bại", type: "error" });
      }
    } catch (e: any) {
      showToast({ message: e?.message || "Tạo phiếu thất bại", type: "error" });
    } finally { setBusy(false); }
  };

  const doAction = async (endPoint: string, okMsg: string) => {
    setBusy(true);
    try {
      const res = await postOrder({ endPoint, data: {} });
      if (res && isSuccess(res)) {
        showToast({ message: okMsg, type: "success" });
        await loadTransfers(); onReload?.();
      } else {
        showToast({ message: (res as any)?.message || "Thao tác thất bại", type: "error" });
      }
    } catch (e: any) {
      showToast({ message: e?.message || "Thao tác thất bại", type: "error" });
    } finally { setBusy(false); }
  };

  // Ở INSPECTING: bắt buộc chi nhánh gốc đã "Kiểm tra phân loại" xong mới cho điều chuyển — tránh chi nhánh
  // nhận phải tự làm thay phân loại của chi nhánh gốc (khớp rule đã áp dụng ở mobile/backend). Đơn giặt lại
  // (order_type=2) dùng item_count làm tín hiệu đã phân loại thay vì classification_category.
  const isReWashOrder = Number((order as any).order_type) === 2;
  const hasClassification = isReWashOrder
    ? Number((order as any).item_count) > 0
    : !!order.classification_category;
  // Cho phép điều chuyển ĐƠN CON (parent_order_id có) — chỉ chặn ĐƠN CHA đã chia (has_sub_orders=1).
  const canCreate = isSupervisor && !active && order.has_sub_orders !== 1
    && ELIGIBLE_STATUSES.has(Number(order.status)) && branchAllowed(currentBranchId)
    && (Number(order.status) !== ORDER_STATUS.INSPECTING || hasClassification);
  // Loại A (tạo từ WAITING_ACCEPT — đơn chưa ai lấy hàng): không có bước "Đã giao xe" (không có gì để chở vật
  // lý), nhận thẳng từ PENDING. Loại B: tài xế "Nhận vận chuyển" QUA APP tự động chuyển PENDING→IN_TRANSIT
  // luôn (không cần ai bấm "Đã giao xe" thủ công nữa) — nhận chỉ khi ĐÃ có tài xế vận chuyển
  // (transport_driver_id, tự claim hoặc giám sát chỉ định qua app). Web chỉ hiển thị trạng thái chờ tài xế,
  // KHÔNG còn nút "Đã giao xe vận chuyển" thủ công nữa (khớp mobile — BE `markTransferInTransit` vẫn giữ
  // nguyên để mở lại sau nếu cần).
  const isEarlyTransfer = active?.pre_transfer_status === ORDER_STATUS.WAITING_ACCEPT;
  const canSend = false;
  const canReceive = active && isSupervisor && branchAllowed(active.to_branch_id) && (
    isEarlyTransfer
      ? (active.status === TRANSFER_STATUS.PENDING || active.status === TRANSFER_STATUS.IN_TRANSIT)
      : (active.status === TRANSFER_STATUS.IN_TRANSIT && !!active.transport_driver_id)
  );
  const canCancel = active && isSupervisor && active.status !== TRANSFER_STATUS.RECEIVED && branchAllowed(active.from_branch_id);

  // Không hiện gì nếu không có phiếu và không đủ điều kiện tạo
  if (!active && !canCreate && transfers.length === 0) return null;

  return (
    <div className="bg-orange-50 rounded-lg p-4 mb-4 border border-orange-200">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <p className="text-sm font-medium text-orange-800">Điều chuyển chi nhánh</p>
        </div>
        {canCreate && !showCreate && (
          <button onClick={openCreate} className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition">
            Điều chuyển
          </button>
        )}
      </div>

      {/* Phiếu đang hoạt động */}
      {active && (
        <div className="bg-white rounded-lg p-3 border border-orange-100 mb-2">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <span>{active.from_branch_name || `CN #${active.from_branch_id}`}</span>
              <span className="text-slate-400">→</span>
              <span>{active.to_branch_name || `CN #${active.to_branch_id}`}</span>
              {active.direction === TRANSFER_DIRECTION.RETURN && <span className="text-xs text-amber-700">(trả về gốc)</span>}
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[active.status] || ""}`}>
              {getTransferStatusLabel(active.status, active.pre_transfer_status)}
            </span>
          </div>
          {!!active.manifest?.length && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {active.manifest.map((it, idx) => (
                <span key={idx} className="text-xs bg-slate-100 px-2 py-0.5 rounded">{it.category}: <b>{it.quantity}</b></span>
              ))}
            </div>
          )}
          {active.sent_at && <p className="text-xs text-slate-500 mt-1">Giao xe lúc: {formatServerDateTime(active.sent_at)}</p>}
          {!isEarlyTransfer && active.status === TRANSFER_STATUS.IN_TRANSIT && (
            active.transport_driver_id
              ? <p className="text-xs text-slate-500 mt-1">Tài xế vận chuyển: <b>{active.transport_driver_name || `#${active.transport_driver_id}`}</b></p>
              : <p className="text-xs text-amber-600 mt-1">Đang chờ tài xế nhận vận chuyển (qua app) — chưa xác nhận đã nhận được.</p>
          )}
          {active.note && <p className="text-xs text-slate-500 mt-1">Ghi chú: {active.note}</p>}

          <div className="flex gap-2 mt-2">
            {canSend && (
              <button disabled={busy} onClick={() => doAction(API_ORDER_ROUTER.API_TRANSFER_IN_TRANSIT(active.id), "Đã ghi nhận giao xe vận chuyển")}
                className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition disabled:opacity-50">
                Đã giao xe vận chuyển
              </button>
            )}
            {canReceive && (
              <button disabled={busy} onClick={() => doAction(API_ORDER_ROUTER.API_TRANSFER_RECEIVE(active.id), "Đã xác nhận nhận hàng")}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition disabled:opacity-50">
                Xác nhận đã nhận
              </button>
            )}
            {canCancel && (
              <button disabled={busy} onClick={() => doAction(API_ORDER_ROUTER.API_TRANSFER_CANCEL(active.id), "Đã huỷ phiếu điều chuyển")}
                className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition disabled:opacity-50">
                Huỷ phiếu
              </button>
            )}
          </div>
        </div>
      )}

      {/* Form tạo phiếu */}
      {showCreate && (
        <div className="bg-white rounded-lg p-3 border border-orange-100 mb-2 space-y-2">
          <p className="text-sm font-medium text-slate-700">Chọn chi nhánh nhận</p>
          <select className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:outline-none"
            value={selectedBranchId ?? ""} onChange={(e) => setSelectedBranchId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">— Chọn chi nhánh —</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>
                {b.name}{b.id === ownerBranchId && currentBranchId !== ownerBranchId ? " (trả về gốc)" : ""}
              </option>
            ))}
          </select>
          <textarea className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-300 focus:outline-none resize-none"
            rows={2} placeholder="Ghi chú (không bắt buộc)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex justify-end gap-2">
            <button disabled={busy} onClick={() => { setShowCreate(false); setSelectedBranchId(null); setNote(""); }}
              className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50 transition">Huỷ</button>
            <button disabled={busy || !selectedBranchId} onClick={doCreate}
              className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition disabled:opacity-50">
              Tạo phiếu
            </button>
          </div>
        </div>
      )}

      {/* Timeline lịch sử phiếu */}
      {transfers.length > 0 && (
        <div className="mt-1">
          <p className="text-xs font-medium text-slate-500 mb-1">Lịch sử điều chuyển</p>
          <div className="space-y-1">
            {transfers.map(t => (
              <div key={t.id} className="flex items-center justify-between text-xs text-slate-600 bg-white/60 rounded px-2 py-1">
                <span>
                  {t.from_branch_name || `CN #${t.from_branch_id}`} → {t.to_branch_name || `CN #${t.to_branch_id}`}
                  {t.received_at ? ` · nhận ${formatServerDateTime(t.received_at)}` : t.sent_at ? ` · gửi ${formatServerDateTime(t.sent_at)}` : t.created_at ? ` · tạo ${formatServerDateTime(t.created_at)}` : ""}
                </span>
                <span className={`px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[t.status] || ""}`}>{getTransferStatusLabel(t.status, t.pre_transfer_status)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <p className="text-xs text-slate-400 mt-1">Đang tải...</p>}
    </div>
  );
};

export default OrderTransferSection;
